import { DatePipe, LowerCasePipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Signal,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  AiUserDataGroup,
  ContextService,
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  GlobalServicesLoginDetails,
  LeadStatus,
  UserRights,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_BUTTONS } from '@maximizer/core/shared/ui/components/buttons';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import {
  AppDirectoryService,
  EmailSubscription,
  EmailSyncService,
  OutlookSyncEntityType,
  UserSyncConfiguration,
} from '@maximizer/outlook-sync/data-access';
import { InteractionService } from '@maximizer/outlook/interaction-log/data-access';
import { EmailDetailComponent } from '@maximizer/outlook/interaction-log/ui';
import { StepGuideComponent } from '@maximizer/outlook/onboarding/ui';
import { OpportunityService } from '@maximizer/outlook/opportunity/data-access';
import {
  AbEntryService,
  EmailService,
  EntryLinkService,
  EntryStore,
  LeadService,
  OnboardingService,
  OnboardingStepNumber,
  OnboardingStore,
  OutlookService,
  OutlookStore,
  PermissionMessage,
  StepGuideId,
  TrackerService,
  Vertical,
  VerticalService,
  buildAccountLink,
  getPermissionString,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryLastContactDatePreference,
  AbEntryType,
  EmailEntryType,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OpportunityRevenueSum,
  OutlookAbEntryDetails,
  OutlookEmail,
  OutlookEmailDetail,
  OutlookLeadSearch,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  ONBOARDING_GUIDE_OFFSETS,
  ONBOARDING_STEP_POPOVER_OPEN_DELAY,
  ONBOARDING_STEP_POPOVER_WIDTH,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { SyncInstallComponent } from '@maximizer/outlook/sync/ui';
import { TimelineComponent } from '@maximizer/outlook/timeline/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  DropDownButtonComponent,
  KENDO_BUTTONS,
} from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LAYOUT, SelectEvent } from '@progress/kendo-angular-layout';
import {
  KENDO_TOOLTIPS,
  PopoverContainerDirective,
  Position,
} from '@progress/kendo-angular-tooltip';
import {
  Observable,
  Subscription,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { EntryDetailsComponent } from '../entry-details/entry-details.component';
import { EntrySummaryComponent } from '../entry-summary/entry-summary.component';
import { AiFeaturesService, isOnForUser } from '@maximizer/ai/data-access';

@Component({
  selector: 'maximizer-entry-page',
  templateUrl: './entry-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    EmailDetailComponent,
    EntryDetailsComponent,
    EntrySummaryComponent,
    FormsModule,
    KENDO_BUTTONS,
    KENDO_DIALOG,
    KENDO_INPUTS,
    KENDO_LAYOUT,
    KENDO_LOADER,
    KENDO_TOOLTIPS,
    LowerCasePipe,
    NgClass,
    OUTLOOK_SHARED_UI,
    RouterLink,
    StepGuideComponent,
    SyncInstallComponent,
    TimelineComponent,
    TranslatePipe,
    UI_BUTTONS,
    UI_KENDO,
    UI_LOADER,
    UI_PIPES,
  ],
})
export class EntryPageComponent
  extends DisposableComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly translate = inject(TranslateService);
  private readonly abentryService = inject(AbEntryService);
  private readonly leadService = inject(LeadService);
  private readonly contextService = inject(ContextService);
  private readonly entryLinkService = inject(EntryLinkService);
  private readonly interactionService = inject(InteractionService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly outlookService = inject(OutlookService);
  private readonly outlookSyncService = inject(EmailSyncService);
  private readonly insightsService = inject(InsightsService);
  private readonly router = inject(Router);
  private readonly oAuthService = inject(OAuth2Service);
  private readonly emailService = inject(EmailService);
  private readonly tracker = inject(TrackerService);
  private readonly appDirectoryService = inject(AppDirectoryService);
  private readonly verticalService = inject(VerticalService);
  private readonly feature = inject(FeatureService);
  private readonly aiFeaturesService = inject(AiFeaturesService);

  public readonly onboardingService = inject(OnboardingService);
  public readonly globalStore = inject(GlobalStore);
  public readonly outlookStore = inject(OutlookStore);
  public readonly onboardingStore = inject(OnboardingStore);
  public readonly viewContainerRef = inject(ViewContainerRef);
  public readonly entryStore = inject(EntryStore);

  public readonly cdn = inject<string>(CDN_URL);

  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');
  timelineComponent = viewChild<TimelineComponent>('timelineComponent');
  moreActionsDropdown = viewChild<DropDownButtonComponent>(
    'moreActionsDropdown',
  );
  entryActionsDropdown = viewChild<DropDownButtonComponent>(
    'entryActionsDropdown',
  );
  entryPageContainer = viewChild.required<ElementRef>('entryPageContainer');

  // Onboarding
  entryActions2Container = viewChild<PopoverContainerDirective>(
    'entryActions2Container',
  );
  entryActions2Anchor = viewChild<Element>('entryActions2Anchor');

  entryActions3Container = viewChild<PopoverContainerDirective>(
    'entryActions3Container',
  );
  entryActions3Anchor = viewChild<Element>('entryActions3Anchor');

  type = input.required<EmailEntryType>();
  id = input.required<string>();

  leadKey = computed(() => {
    return this.type() === EntryType.Lead ? decodeURI(this.id()) : null;
  });

  abEntryKey = computed(() => {
    return this.type() === EntryType.AbEntry ? decodeURI(this.id()) : null;
  });

  currentId = computed(() => {
    return decodeURI(this.id());
  });

  userRights = computed<UserRights | undefined>(() => {
    return this.globalStore.session()?.user.rights;
  });

  isEmailSaved(): boolean {
    return !this.loading.getEmail && !!this.outlookEmail?.emailSaved;
  }

  private readonly scrollThreshold = 100;
  private lastScrollTop = 0;

  readonly stepPopoverWidth = ONBOARDING_STEP_POPOVER_WIDTH;
  readonly OnboardingGuideOffsets = ONBOARDING_GUIDE_OFFSETS;

  EntryType = EntryType;
  Vertical = Vertical;
  pageTitle = '';
  deepLink = '';
  oppLink = '';
  csLink = '';
  companyDeepLink = '';
  entryType = '';
  abentry?: OutlookAbEntryDetails;
  lead?: OutlookLeadSearch;
  leadStatus = LeadStatus;
  opportunities?: OpportunityRevenueSum;
  resourceName = '';
  disabled = false;
  showSaveEmail = false;
  subscription?: Subscription;
  outlookEmail?: OutlookEmail;
  showAutoSaveEmailToggle = false;
  showAutoSaveLimitDialog = false;
  autoSave = false;
  scrollToTopVisible = false;
  syncConfiguration?: UserSyncConfiguration;
  outlookSyncInstalled = false;
  emailSyncEnabled = false;
  emailSyncSaveOptionEnabled = false;
  logInDetails?: GlobalServicesLoginDetails | null;
  emailSaveUserRights = true;
  popoverPosition: Position = 'bottom';
  tooltipPosition: Position = 'bottom';
  OnboardingStep = OnboardingStepNumber;
  StepGuideId = StepGuideId;
  saveEmailDetail = false;
  saveEmailDetailProperties?: OutlookEmailDetail;
  moreActions!: Signal<MenuItem[]>;
  entryActions: MenuItem[] = [];

  lastContactDate = signal<Date | null>(null);
  isTimelineTab = signal(false);

  onboardingGuide = computed(() => this.onboardingStore.inProgress());
  isAbEntry = computed(() => this.type() === EntryType.AbEntry);

  autoSaveDisabled = computed(() => {
    const config = this.outlookStore.syncConfiguration();
    const emailsSync = config?.emailsSync;

    if (!emailsSync?.enabled) {
      return true;
    }

    return false;
  });

  loading = {
    sync: false,
    skeleton: false,
    saveEmail: false,
    getEmail: true,
  };

  autoSaveTooltipMode = computed<'default' | 'syncEverything' | 'disabled'>(
    () => {
      if (!this.emailSaveUserRights) {
        return 'default';
      }

      if (this.autoSaveDisabled()) {
        return 'disabled';
      }

      return this.outlookStore.syncConfiguration()?.emailsSync?.syncEverything
        ? 'syncEverything'
        : 'default';
    },
  );

  // AI feature flags and settings
  outlookAiIqBoostEnabled = signal(false);
  aiIqBoostShown = signal(false);

  aiEnabled = signal(false);
  aiIqBoostOnForUser = signal(false);

  aiIqBoostFeatureFlagsEnabled = computed(
    () => this.outlookAiIqBoostEnabled() && this.aiIqBoostShown(),
  );

  aiIqBoostEnabled = computed(
    () =>
      this.aiEnabled() &&
      this.aiIqBoostOnForUser() &&
      this.aiIqBoostFeatureFlagsEnabled(),
  );

  constructor() {
    super();

    this.outlookAiIqBoostEnabled.set(
      this.feature.isFeatureOn('integration-o365-ai-iq-boost-enabled', false),
    );

    this.aiIqBoostShown.set(
      this.feature.isFeatureOn('core-ai-iq-boost-shown', false),
    );

    this.showAutoSaveEmailToggle = this.feature.isFeatureOn(
      'microsoft-outlook-sync-emails-shown',
      false,
    );

    this.moreActions = computed<MenuItem[]>(() => {
      const items: MenuItem[] = [
        {
          id: 'add-call',
          tag: 'AddCall',
          text: this.translate.instant('outlook.entryPage.actions.addCallLog'),
          icon: 'maximizer-icons icon-add-call icon-14',
          click: () => this.actionNavigate('call'),
          disabled: !this.userRights()?.interactionLog?.insert,
          title: 'outlook.callLog.callLogs',
        },
        {
          id: 'add-appointment',
          tag: 'AddAppointment',
          text: this.translate.instant(
            'outlook.entryPage.actions.addAppointment',
          ),
          icon: 'maximizer-icons icon-add-appointment icon-14',
          click: () => this.actionNavigate('appointment'),
          disabled: false,
          title: 'outlook.entries.appointment',
        },
      ];

      if (this.aiIqBoostEnabled()) {
        items.push({
          id: 'add-case',
          tag: 'AddCase',
          text: this.translate.instant('outlook.case.addTitle', {
            label: this.verticalService.translateVertical('case'),
          }),
          icon: 'maximizer-icons icon-add-case icon-14',
          click: () => this.actionNavigate('case'),
          disabled: !this.userRights()?.customerService?.insert,
          title: 'cases',
        });
      }

      return items;
    });

    effect(() => {
      const inProgress = this.onboardingGuide();
      const currentGuide = this.onboardingStore.currentGuide();
      const container2 = this.entryActions2Container();
      const anchor2 = this.entryActions2Anchor();
      const container3 = this.entryActions3Container();
      const anchor3 = this.entryActions3Anchor();

      if (inProgress) {
        if (
          currentGuide === StepGuideId.EntryActions3 &&
          container3 &&
          anchor3
        ) {
          container2?.hide();
          container3.show(anchor3);
        } else if (
          currentGuide === StepGuideId.EntryActions2 &&
          container2 &&
          anchor2
        ) {
          container3?.hide();
          container2.show(anchor2);
        }
      } else {
        container2?.hide();
        container3?.hide();
      }
    });
  }

  ngOnInit(): void {
    this.setUserRights();

    if (!this.type()) {
      this.entryNotFound();
      return;
    }

    this.showSaveEmail =
      !this.outlookService.isCompose && !this.outlookService.isCalendarItem;
    this.decomposeId();
    this.loadData();
    this.logInDetails = this.oAuthService.getStorageLoginDetails();

    this.outlookStore.loadUsers();
  }

  ngAfterViewInit(): void {
    this.setEntryTypeProperties();
  }

  override ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private setUserRights(): void {
    if (this.userRights()) {
      if (!this.userRights()?.interactionLog?.insert) {
        const menuItem = this.moreActions().find(
          (item) => item.id === 'add-call',
        );
        if (menuItem) {
          menuItem.disabled = true;
        }
      }

      this.emailSaveUserRights = this.getSaveEmailPermission(this.type());
    }
  }

  private setEntryTypeProperties(): void {
    const notification = this.notification();
    if (this.entryType === EntryType.Individual) {
      let editionLabel = this.verticalService.translateVertical('individual');
      notification.resourceId = editionLabel;
      this.pageTitle = editionLabel;
    } else {
      notification.resourceId = this.entryType ?? EntryType.Contact;
      this.pageTitle = this.translate.instant(
        'outlook.entries.' + (this.entryType ?? EntryType.Contact),
      );
    }

    if (this.onboardingStore.inProgress()) {
      const entryPageContainer = this.entryPageContainer();
      const containerHeight = entryPageContainer.nativeElement.offsetHeight;
      if (containerHeight < 450) {
        this.popoverPosition = 'top';
      }
    }
  }

  private decomposeId(): void {
    try {
      const currentId = this.currentId();
      const indexQuestion = currentId.indexOf('?');
      let processedId = currentId;
      if (indexQuestion > -1) {
        processedId = currentId.substring(0, indexQuestion);
      }
      const decomposedId = globalThis.atob(processedId);
      const decomposedItems = decomposedId.split('\t');
      this.entryType = decomposedItems[0]?.toLocaleLowerCase();
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
      this.entryNotFound();
    }
  }

  actionNavigate(action: 'call' | 'appointment' | 'case') {
    this.closeMoreActionsDropdown();

    if (action === 'call' && this.userRights()?.interactionLog?.insert) {
      this.router.navigate([`/${this.type()}/${this.id()}/add-call-log`]);
    } else if (action === 'appointment') {
      this.router.navigate([`/${this.type()}/${this.id()}/add-appointment`]);
    } else if (
      action === 'case' &&
      this.userRights()?.customerService?.insert
    ) {
      this.router.navigate([`/${this.type()}/${this.id()}/add-case`]);
    }
  }

  private entryNotFound() {
    const notification = this.notification();
    notification.resourceId = this.entryType ?? EntryType.Contact;
    notification.show('load', 'error');
    this.disabled = true;
    this.router.navigate(['/home']);
  }

  private loadData() {
    this.loading.skeleton = true;

    const opportunity =
      this.type() === EntryType.AbEntry && this.userRights()?.opportunities.read
        ? this.opportunityService.getOpenAndWonSum(this.currentId())
        : of(null);

    const entry =
      this.type() === EntryType.Lead
        ? this.leadService.getByKey(this.currentId())
        : this.abentryService.getAllDetailsByKey(this.currentId());

    forkJoin({
      entry,
      lastContactDate:
        this.type() === EntryType.Lead
          ? this.interactionService.getLastContact(this.currentId())
          : of(null),
      opportunityDetails: opportunity,
      aiEnabled: this.aiIqBoostFeatureFlagsEnabled()
        ? this.aiFeaturesService.getEnabled()
        : of(false),
    })
      .pipe(
        switchMap((data) => {
          this.aiEnabled.set(data.aiEnabled ?? false);

          const aiFeatureSettings = data.aiEnabled
            ? this.getAIFeatureSettings()
            : of(null);

          return forkJoin({ aiFeatureSettings }).pipe(
            map(({ aiFeatureSettings }) => ({ ...data, aiFeatureSettings })),
          );
        }),
        catchError(async () => {
          this.notification().show('load', 'error');
          this.disabled = true;
        }),
        finalize(() => {
          this.loading.skeleton = false;
          this.disabled = !this.lead && !this.abentry;

          if (this.onboardingStore.inProgress()) {
            this.initializeOnboardingState();
          }
        }),
      )
      .subscribe(async (data) => {
        if (!data) return;

        const aiSettings = data.aiFeatureSettings;
        this.aiIqBoostOnForUser.set(
          !!aiSettings &&
            isOnForUser({
              orgAvailable: aiSettings.admin.aiFeaturesEnabled.iqBoost,
              userEnabled: aiSettings.user.aiFeaturesEnabled.iqBoost,
            }),
        );

        this.populateDeepLinks();

        if (
          this.showAutoSaveEmailToggle &&
          this.outlookStore.versionValidForNewSync() &&
          this.outlookStore.isCloud()
        ) {
          this.readOutlookSyncSettings();
        }

        if (this.type() === EntryType.Lead) {
          await this.loadLead(
            data.entry as OutlookLeadSearch,
            data.lastContactDate,
          );
        }

        if (this.type() === EntryType.AbEntry) {
          await this.loadAbEntry(data.entry as OutlookAbEntryDetails);
        }

        if (data.opportunityDetails) {
          this.opportunities = data.opportunityDetails;
        }
      });
  }

  private getAIFeatureSettings(): Observable<AiUserDataGroup | null> {
    const session = this.globalStore.session();
    if (!session) {
      return of(null);
    }

    return this.aiFeaturesService
      .getAIUserData(session)
      .pipe(catchError(() => of(null)));
  }

  populateDeepLinks(): void {
    const accountLink = this.getAccountLink();
    this.deepLink = `${accountLink}?ss=KEY(${this.id()})`;
    this.oppLink = `${accountLink}?ssopp=KEY(${this.id()})`;
    this.csLink = `${accountLink}?sscscase=KEY(${this.id()})`;
  }

  private getAccountLink(): string {
    const session = this.globalStore.session();
    if (!session) {
      return '';
    }

    return buildAccountLink(
      this.contextService.website,
      session.alias,
      !!this.outlookStore.isCloud(),
    );
  }

  private readOutlookSyncSettings(): void {
    const config = this.outlookStore.syncConfiguration();
    if (!config) return;

    const { enabled, emailsSync, id } = config;

    this.syncConfiguration = config;
    this.outlookSyncInstalled = !!enabled;
    this.emailSyncEnabled = !!emailsSync?.enabled;

    if (emailsSync.enabled) {
      this.emailSyncSaveOptionEnabled = true;
    }

    if (id) {
      this.fetchEmailSubscription(id);
    }
  }

  private fetchEmailSubscription(syncId: string): void {
    if (!this.syncConfiguration?.emailsSync.enabled) {
      this.autoSave = false;
      return;
    }

    this.loading.sync = true;

    if (this.syncConfiguration?.emailsSync.syncEverything) {
      this.outlookSyncService
        .getEmailExclusionList(syncId)
        .pipe(
          map((response) =>
            response.items.some(
              (item) => item.maximizerKey === this.currentId(),
            ),
          ),
          catchError(() => of(false)),
          finalize(() => {
            this.loading.sync = false;
          }),
        )
        .subscribe((isExcluded) => {
          this.autoSave = !isExcluded;
        });
      return;
    }

    this.outlookSyncService
      .getEmailSubscriptionByKey(this.currentId(), syncId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
        map((emailSubscription) => {
          this.autoSave = emailSubscription?.enabled ?? false;
        }),
        finalize(() => {
          this.loading.sync = false;
        }),
      )
      .subscribe();
  }

  private async loadAbEntry(abentry: OutlookAbEntryDetails) {
    if (abentry.type === EntryType.Company) {
      abentry.name = abentry.companyName;
      abentry.companyName = '';
    }

    this.abentry = abentry;
    this.entryStore.setEntry(abentry);
    this.entryStore.setType(EntryType.AbEntry);

    this.lastContactDate.set(abentry.lastContactDateValue);

    if (abentry.parentKey) {
      const accountLink = this.getAccountLink();
      this.companyDeepLink = `${accountLink}?ss=KEY(${abentry.parentKey})`;
    }

    this.buildEntryActions();

    try {
      this.outlookEmail = {
        id: abentry.key ?? this.currentId(),
        emailAddress: abentry.emails?.[0]?.value ?? '',
        displayName: abentry.name ?? '',
        type: EntryType.AbEntry,
        abentryType: abentry.type?.toLocaleLowerCase() as
          AbEntryType | undefined,
        parentKey: abentry.parentKey ?? undefined,
      };

      if (!this.showSaveEmail) return;
      if (this.outlookEmail) {
        this.outlookEmail.emailSaved =
          await this.outlookService.checkSavedEmailByKey(this.currentId());
      }
    } catch (error) {
      this.insightsService.trackException(
        { exception: error as Error },
        { message: 'Error on load abentry' },
      );
    } finally {
      this.loading.getEmail = false;
    }
  }

  private async loadLead(
    lead: OutlookLeadSearch,
    lastContactDate: Date | null,
  ) {
    try {
      this.lead = lead;
      this.entryStore.setEntry(lead);
      this.entryStore.setType(EntryType.Lead);

      this.lastContactDate.set(lastContactDate);
      this.deepLink = this.deepLink?.replace('?ss=KEY', '?sslead=KEY');

      this.buildEntryActions();

      this.outlookEmail = {
        id: this.currentId(),
        emailAddress: lead.email ?? '',
        displayName: lead.name,
        type: EntryType.Lead,
        abentryType: undefined,
      };

      if (!this.showSaveEmail) return;
      const isSaved = await this.outlookService.checkSavedEmailByKey(
        this.currentId(),
      );
      if (this.outlookEmail) {
        this.outlookEmail.emailSaved = isSaved;
      }
    } catch (error) {
      this.insightsService.trackException(
        { exception: error as Error },
        { message: 'Error on load lead' },
      );
    } finally {
      this.loading.getEmail = false;
    }
  }

  async saveEmail(): Promise<void> {
    if (this.outlookEmail?.id === undefined || !this.emailSaveUserRights) {
      return;
    }

    if (this.outlookStore.preferences().saveEmailDetail) {
      this.openSaveEmailDetails(this.currentId(), this.type());
    } else {
      this.loading.saveEmail = true;
      let isSuccessful = false;

      try {
        const emlFile = await this.outlookService.getReadEmlFile();
        if (!emlFile) throw new Error('Unable to get eml file');

        const documentId = await this.emailService.saveEmailToSingleEntry(
          emlFile,
          this.currentId(),
        );

        if (!documentId) {
          this.notification().showMessage(
            'error',
            'outlook.emailNotification.failEntry',
            true,
          );
          return;
        }

        if (
          this.type() === EntryType.AbEntry &&
          this.outlookStore.isSentFolder()
        ) {
          this.handleLastContactDate(this.outlookEmail);
        }

        this.outlookEmail.emailSaved = true;
        isSuccessful = true;
        this.notification().showMessage(
          'success',
          'outlook.emailNotification.successEntry',
          true,
        );

        await this.outlookService.saveKeysInOutlook([this.outlookEmail], false);

        if (this.isTimelineTab()) {
          this.timelineComponent()?.refresh();
        }
      } catch (error) {
        console.error('Error', error);
        this.notification().showMessage(
          'error',
          'outlook.emailNotification.failEntry',
          true,
        );
      } finally {
        this.loading.saveEmail = false;
        this.trackEmailSaveResult(isSuccessful);
      }
    }
  }

  private openSaveEmailDetails(key: string, type: EmailEntryType): void {
    const item = this.outlookService.mailboxItem;
    const entryType = this.mapEmailEntryToType(type);

    this.saveEmailDetail = true;
    this.saveEmailDetailProperties = {
      subject: item?.subject ?? '',
      type: entryType,
      key,
      abentryType:
        this.type() === EntryType.AbEntry
          ? (this.abentry?.type as AbEntryType)
          : undefined,
    };
  }

  async onSaveEmailDetail(): Promise<void> {
    this.saveEmailDetail = false;
    this.saveEmailDetailProperties = undefined;

    if (this.outlookEmail) {
      this.outlookEmail.emailSaved = true;
      await this.outlookService.saveKeysInOutlook([this.outlookEmail], false);

      if (this.isTimelineTab()) {
        this.timelineComponent()?.refresh();
      }

      if (
        this.type() === EntryType.AbEntry &&
        this.outlookStore.isSentFolder()
      ) {
        this.handleLastContactDate(this.outlookEmail);
      }
    }

    this.notification().showMessage(
      'success',
      'outlook.emailNotification.successEntry',
      true,
    );
  }

  private mapEmailEntryToType(type: EmailEntryType): EntryType {
    return type === EntryType.AbEntry ? EntryType.AddressBook : EntryType.Lead;
  }

  closeEmailSyncLimitDialog() {
    this.showAutoSaveLimitDialog = false;
  }

  reviewListEmailSyncDialog() {
    if (!this.logInDetails) {
      this.notification().showMessage('error', 'outlook.errors.appStore', true);
      return;
    }
    this.appDirectoryService
      .buildAppStoreFormRequest(this.logInDetails, false)
      .pipe(
        catchError(() => {
          this.notification().showMessage(
            'error',
            'outlook.errors.appStore',
            true,
          );
          return of(null);
        }),
        finalize(() => (this.showAutoSaveLimitDialog = false)),
      )
      .subscribe();
  }

  onAutoSaveEmailChange(isEnabled: boolean): void {
    this.tracker.trackEvent({
      module: EventModule.EntryDetails,
      journey: EventJourney.AutoSaveToggle,
      action: EventAction.Clicked,
      metadata: {
        entry: this.entryType,
        autoSave: isEnabled ? EventMetadata.On : EventMetadata.Off,
      },
    });

    if (!this.syncConfiguration) {
      return;
    }

    this.loading.sync = true;

    if (this.syncConfiguration.emailsSync.syncEverything) {
      this.handleExclusionListToggle(isEnabled);
      return;
    }

    if (isEnabled) {
      this.handleUpdateEmailAutoSaveWithCount();
    } else {
      this.handleUpdateEmailAutoSave(false);
    }
  }

  private handleExclusionListToggle(isEnabled: boolean): void {
    if (!this.syncConfiguration) {
      return;
    }

    const entityType =
      this.type() === EntryType.AbEntry
        ? OutlookSyncEntityType.AbEntry
        : OutlookSyncEntityType.Lead;

    const request$ = isEnabled
      ? this.outlookSyncService.removeFromEmailExclusionList(
          this.syncConfiguration.id,
          this.currentId(),
          entityType,
        )
      : this.outlookSyncService.addToEmailExclusionList(
          this.syncConfiguration.id,
          this.currentId(),
          entityType,
        );

    const successKey = isEnabled
      ? 'outlook.entryPage.autoSave.exclusionList.removed'
      : 'outlook.entryPage.autoSave.exclusionList.added';
    const errorContext = isEnabled ? 'removing from' : 'adding to';

    request$
      .pipe(
        catchError((error) => {
          this.autoSave = !isEnabled;
          this.notification().showMessage(
            'error',
            'outlook.entryPage.autoSave.error',
            true,
          );
          console.error(`Error ${errorContext} exclusion list:`, error);
          this.insightsService.trackException(
            { error },
            { message: `Failed ${errorContext} email exclusion list` },
          );
          return of(false);
        }),
        finalize(() => {
          this.loading.sync = false;
        }),
      )
      .subscribe((success) => {
        if (!success) return;

        this.autoSave = isEnabled;
        const msg = this.translate.instant(successKey, {
          entryType: this.translate.instant(
            'outlook.entries.' + this.entryType,
          ),
        });
        this.notification().showMessage('success', msg);
      });
  }

  private handleUpdateEmailAutoSave(isEnabled: boolean): void {
    if (!this.syncConfiguration) {
      return;
    }
    const entityType: OutlookSyncEntityType =
      this.type() === EntryType.AbEntry
        ? OutlookSyncEntityType.AbEntry
        : OutlookSyncEntityType.Lead;

    const emailSubscription: EmailSubscription = {
      maximizerEntityType: entityType,
      maximizerKey: this.currentId(),
      enabled: isEnabled,
    };

    this.outlookSyncService
      .updateEmailAutoSaveSubscription(
        this.syncConfiguration.id,
        emailSubscription,
      )
      .pipe(
        catchError((error) => {
          this.handleEmailAutoSaveError(isEnabled, error);
          return of(null);
        }),
        finalize(() => {
          this.loading.sync = false;
        }),
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }

        this.autoSave = isEnabled;

        if (isEnabled) {
          this.notification().showMessage(
            'success',
            'outlook.entryPage.autoSave.notification',
            true,
          );
        }
      });
  }
  private handleUpdateEmailAutoSaveWithCount(): void {
    if (!this.syncConfiguration) {
      return;
    }

    this.outlookSyncService
      .getEmailAutoSaveSubscriptionCount(this.syncConfiguration.id)
      .pipe(
        catchError((error) => {
          this.handleEmailAutoSaveError(true, error);
          this.loading.sync = false;
          return of(null);
        }),
      )
      .subscribe((emailCount) => {
        if (!emailCount || !this.syncConfiguration) {
          this.loading.sync = false;
          return;
        }

        if (emailCount.currentCount >= emailCount.maximumAllowed) {
          this.showAutoSaveLimitDialog = true;
          this.autoSave = false;
          this.loading.sync = false;
          return;
        }
        this.handleUpdateEmailAutoSave(true);
      });
  }
  private handleEmailAutoSaveError(isEnabled: boolean, error: Error): void {
    this.autoSave = !isEnabled;
    this.notification().showMessage(
      'error',
      'outlook.entryPage.autoSave.error',
      true,
    );

    console.error('Error updating auto-save subscription:', error);
    this.insightsService.trackException(
      { error },
      { message: 'Failed updating auto-save subscription' },
    );
  }

  getSaveEmailPermission(type: EmailEntryType): boolean {
    if (
      (type === EntryType.AbEntry && !this.userRights()?.addressBook.update) ||
      (type === EntryType.Lead && !this.userRights()?.lead.update) ||
      !this.userRights()?.documents?.insert
    ) {
      return false;
    }
    return true;
  }

  getSaveEmailPermissionMessage(
    type: EmailEntryType,
    message: PermissionMessage,
  ): string {
    if (type === EntryType.AbEntry && !this.userRights()?.addressBook.update) {
      return getPermissionString(
        this.translate,
        EntryType.AbEntry,
        'edit',
        message,
      );
    } else if (type === EntryType.Lead && !this.userRights()?.lead.update) {
      return getPermissionString(
        this.translate,
        EntryType.Lead,
        'edit',
        message,
      );
    } else if (this.userRights()?.documents?.insert) {
      return getPermissionString(
        this.translate,
        undefined,
        undefined,
        'generic',
      );
    } else {
      return getPermissionString(
        this.translate,
        'document',
        'create',
        'default',
      );
    }
  }

  // Onboarding
  initializeOnboardingState(): void {
    setTimeout(() => {
      this.showOnboardingGuide();
    }, ONBOARDING_STEP_POPOVER_OPEN_DELAY);
  }

  showOnboardingGuide(): void {
    const currentStep = this.onboardingStore.currentStep();

    if (currentStep === OnboardingStepNumber.Step3) {
      const currentGuide = this.onboardingStore.currentGuide();
      if (currentGuide === StepGuideId.EntryActions3) {
        const container = this.entryActions3Container();
        const anchor = this.entryActions3Anchor();
        if (container && anchor) {
          container.show(anchor);
        }
      } else {
        this.onboardingStore.setCurrentGuide(StepGuideId.EntryActions2);
        const container = this.entryActions2Container();
        const anchor = this.entryActions2Anchor();
        if (container && anchor) {
          container.show(anchor);
        }
      }
    } else if (currentStep === OnboardingStepNumber.Step4) {
      this.onboardingService.isLastStep.set(true);
      this.onboardingService.showOnboardingDialog.set(true);
    }
  }

  onMoreActionsOpen(): void {
    this.closeTooltips();
    if (this.onboardingStore.currentGuide() === StepGuideId.EntryActions2) {
      this.entryActions2Container()?.hide();
    }
  }

  onMoreActionsClose(): void {
    if (this.onboardingStore.currentGuide() === StepGuideId.EntryActions2) {
      const container = this.entryActions2Container();
      const anchor = this.entryActions2Anchor();
      if (container && anchor) {
        container.show(anchor);
      }
    }
  }

  nextOnboardingStep(): void {
    if (this.onboardingStore.currentGuide() === StepGuideId.EntryActions2) {
      this.entryActions2Container()?.hide();

      const toggleVisible =
        this.outlookStore.versionValidForNewSync() &&
        this.outlookStore.isCloud() &&
        this.showAutoSaveEmailToggle &&
        !this.outlookStore.isSharedMailbox();

      if (!toggleVisible) {
        this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step4);
        this.onboardingService.isLastStep.set(true);
        this.onboardingService.showOnboardingDialog.set(true);
        return;
      }

      const guide3Id = this.syncConfiguration?.enabled
        ? StepGuideId.EntryActions3Alt
        : StepGuideId.EntryActions3;
      this.onboardingStore.setCurrentGuide(guide3Id);
      const container = this.entryActions3Container();
      const anchor = this.entryActions3Anchor();
      if (container && anchor) {
        container.show(anchor);
      }
    } else {
      this.entryActions3Container()?.hide();
      this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step4);
      this.onboardingService.isLastStep.set(true);
      this.onboardingService.showOnboardingDialog.set(true);
    }
  }

  getTooltipPosition(): Position {
    if (
      this.onboardingStore.inProgress() &&
      this.popoverPosition === 'bottom'
    ) {
      return 'top';
    } else {
      return 'bottom';
    }
  }

  public isUser(email: string): boolean {
    return this.outlookStore.userEmails().has(email.toLowerCase());
  }

  private trackEmailSaveResult(success: boolean): void {
    let entityType: EntryType | AbEntryType | EmailEntryType;

    if (this.type() === EntryType.AbEntry && this.entryType) {
      entityType = this.entryType as AbEntryType;
    } else {
      entityType = this.type() ?? EntryType.Contact;
    }

    this.tracker.trackEvent({
      module: EventModule.Email,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
        entity: entityType,
        emailDetails: false,
        search: false,
      },
    });
  }

  private handleLastContactDate(entry: OutlookEmail): void {
    const emailCreationDate = this.outlookService.mailboxItem?.dateTimeCreated;
    if (!emailCreationDate || !entry.id) return;

    const entryId = entry.id;
    this.loading.saveEmail = true;

    this.abentryService
      .updateLastContactDateWithPreferences(
        entryId,
        EntryType.AbEntry,
        AbEntryLastContactDatePreference.EmailSentFromOutlook,
        emailCreationDate,
        entry.abentryType,
        entry.parentKey,
      )
      .pipe(
        finalize(() => (this.loading.saveEmail = false)),
        catchError((error) => {
          console.error('Error updating last contact date:', error);
          return of(null);
        }),
      )
      .subscribe((appliedDate) => {
        if (appliedDate) this.lastContactDate.set(appliedDate);
      });
  }

  getAvatarClasses(): Record<string, boolean> {
    const email = this.abentry?.emails?.[0]?.value ?? this.lead?.email ?? '';
    const hasEmail = !!email && email.trim().length > 0;
    const isUserEmail = hasEmail && this.isUser(email);
    const isCompany = this.abentry?.type === EntryType.Company;

    return {
      'avatar-neutral-20 avatar-border':
        !isUserEmail || (isUserEmail && isCompany),
      'avatar-square': !!(
        this.abentry &&
        (this.abentry.type === EntryType.Individual || isCompany)
      ),
      'avatar-inserv-10 rounded-full': isUserEmail && !isCompany,
    };
  }

  onTabSelect(event: SelectEvent): void {
    const tabTitles = ['summary', 'timeline', 'details'];
    const selectedTab = tabTitles[event.index] || 'unknown';

    // track timeline tab to refresh after email save event
    this.isTimelineTab.set(selectedTab === 'timeline');

    this.tracker.trackEvent({
      module: EventModule.EntryDetails,
      journey: EventJourney.Tab,
      action: EventAction.Clicked,
      metadata: {
        tab: selectedTab,
      },
    });
  }

  onContainerScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const isScrollingDown = scrollTop > this.lastScrollTop;

    if (isScrollingDown) {
      this.closeMoreActionsDropdown();
      this.entryActionsDropdown()?.toggle(false);
    }

    this.lastScrollTop = scrollTop;
    this.scrollToTopVisible = scrollTop > this.scrollThreshold;
  }

  scrollToTop(): void {
    if (this.entryPageContainer()?.nativeElement) {
      this.entryPageContainer()?.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  private closeMoreActionsDropdown(): void {
    const moreActionsDropdown = this.moreActionsDropdown();
    if (moreActionsDropdown?.toggle) {
      moreActionsDropdown.toggle(false);
    }
  }

  navigateToEdit(canEdit: boolean): void {
    if (canEdit) {
      this.tracker.trackEvent({
        module: EventModule.EntryDetails,
        journey: EventJourney.EditEntry,
        action: EventAction.Clicked,
        metadata: {
          entry: this.entryType,
        },
      });

      this.router.navigate([`/${this.type()}/${this.id()}/edit`], {
        queryParams: { entryType: this.entryType },
      });
    }
  }

  openInMaximizer(): void {
    this.tracker.trackEvent({
      module: EventModule.EntryDetails,
      journey: EventJourney.ViewInMaximizer,
      action: EventAction.Clicked,
      metadata: {
        entry: this.entryType,
      },
    });

    window.open(this.deepLink, '_blank', 'noopener');
  }

  async copyEntryLink(): Promise<void> {
    const copied = await this.entryLinkService.copyEntryLink(
      this.id(),
      this.type(),
      this.entryType,
    );

    if (copied) {
      const message = this.translate.instant(
        'outlook.entryPage.actions.entryCopied',
        { type: this.translate.instant(`outlook.entries.${this.entryType}`) },
      );
      setTimeout(() => {
        this.notification().showMessage('success', message, false);
      }, 500);
    } else {
      this.notification().showMessage('error', 'outlook.errors.generic', true);
    }
  }

  buildEntryActions(): void {
    const isLead = !!this.lead;
    const isArchivedLead =
      isLead && this.lead?.status === this.leadStatus.Archived;
    const canEdit = isLead
      ? !!this.userRights()?.lead?.update && !isArchivedLead
      : !!this.userRights()?.addressBook?.update;

    const editTitle = this.getEditTitle(isLead, isArchivedLead);

    this.entryActions = [
      {
        id: 'edit-entry',
        tag: 'Edit',
        text: this.translate.instant('outlook.buttons.edit'),
        icon: 'fa-icons icon-solid icon-14 icon-pencil',
        click: () => this.navigateToEdit(canEdit),
        disabled: !canEdit,
        title: editTitle,
      },
      {
        id: 'view-in-maximizer',
        tag: 'ViewInMaximizer',
        text: this.translate.instant(
          'outlook.entryPage.actions.viewInMaximizer',
        ),
        icon: 'fa-icons icon-solid icon-14 icon-arrow-up-right-from-square',
        click: () => this.openInMaximizer(),
        disabled: false,
      },
      {
        id: 'share-entry',
        tag: 'ShareEntry',
        text: this.translate.instant('outlook.entryPage.actions.shareEntry'),
        icon: 'fa-icons icon-solid icon-14 icon-share-nodes',
        click: () => void this.copyEntryLink(),
        disabled: false,
      },
    ];
  }

  private getEditTitle(
    isLead: boolean,
    isArchivedLead: boolean,
  ): string | undefined {
    if (isLead) {
      if (isArchivedLead) {
        return this.translate.instant('outlook.entryPage.archivedTooltip');
      }
      if (!this.userRights()?.lead?.update) {
        return getPermissionString(
          this.translate,
          EntryType.Lead,
          'edit',
          'default',
        );
      }
      return undefined;
    }

    if (!this.userRights()?.addressBook?.update) {
      return getPermissionString(
        this.translate,
        EntryType.AbEntry,
        'edit',
        'default',
      );
    }
    return undefined;
  }
}
